import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Check,
  FileUp,
  PenLine,
  Sparkles,
  Upload,
  Wand2,
} from "lucide-react";

type OnboardingModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onChooseWizard: () => void;
  onChooseManual: () => void;
  onChooseUpload: () => void;
};

function OnboardingModal({
  isOpen,
  onClose,
  onChooseWizard,
  onChooseManual,
  onChooseUpload,
}: OnboardingModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[800px] p-4 md:p-8">
        <DialogHeader className="text-center mb-2 mt-4">
          <div className="mx-auto w-14 h-14 rounded-full bg-ds-blue-50 hidden md:flex items-center justify-center mb-3">
            <Sparkles className="w-7 h-7 text-ds-blue-600" />
          </div>
          <DialogTitle className="text-xl md:text-2xl font-bold font-heading text-navy-900 text-center">
            Bine ai venit! Cum dorești să începi?
          </DialogTitle>
          <DialogDescription className="text-sm text-ds-text-500 mt-2 max-w-md mx-auto">
            Alege metoda care ți se potrivește. Poți oricând reveni și schimba.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={onChooseWizard}
            className="relative flex flex-col items-start md:items-center text-left md:text-center border-2 border-ds-blue-600 rounded-xl p-5 md:p-6 gap-3 bg-ds-blue-50/30 shadow-md hover:shadow-lg hover:scale-[1.02] hover:border-ds-blue-700 transition-all duration-300 ease-out animate-in fade-in slide-in-from-bottom-2 fill-mode-both"
            style={{ animationDelay: "100ms", animationDuration: "400ms" }}
          >
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-ds-blue-600 text-white text-xs font-bold font-mono px-3 py-1 rounded-full shadow-sm">
              RECOMANDAT
            </div>

            <div className="flex flex-row md:flex-col items-center gap-3 w-full md:w-auto">
              <div className="w-10 h-10 md:w-14 md:h-14 rounded-full bg-ds-blue-50 flex items-center justify-center shadow-md shrink-0">
                <Wand2 className="w-5 h-5 md:w-7 md:h-7 text-ds-blue-600" />
              </div>

              <h3 className="font-bold font-heading text-navy-900 text-base text-left md:text-center">
                Completează automat
              </h3>
            </div>

            <p className="text-xs text-ds-text-500 leading-relaxed text-left md:text-center">
              Ghidat pas cu pas. Datele se completează automat din CUI.
            </p>

            <div className="space-y-1.5 text-left w-full">
              <div className="flex items-center gap-2 text-xs text-ds-text-600">
                <Check className="w-3.5 h-3.5 text-ds-green-500 shrink-0" />
                <span>Auto-completare date ANAF</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-ds-text-600">
                <Check className="w-3.5 h-3.5 text-ds-green-500 shrink-0" />
                <span>Identificare servicii cu AI</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-ds-text-600">
                <Check className="w-3.5 h-3.5 text-ds-green-500 shrink-0" />
                <span>Clasificare NIS2 automată</span>
              </div>
            </div>

            <div className="w-full pt-2 mt-auto">
              <Button className="w-full !text-xs" size="sm">
                Generează notificare
                <ArrowRight className="w-4 h-4" />
              </Button>
              <p className="text-xs font-mono text-ds-text-400 italic mt-1">
                timp estimat: &lt;5min
              </p>
            </div>
          </button>

          <button
            onClick={onChooseManual}
            className="flex flex-col items-start md:items-center text-left md:text-center border border-ds-border-200 rounded-xl p-5 md:p-6 gap-3 bg-white hover:shadow-md hover:scale-[1.02] hover:border-ds-border-300 transition-all duration-300 ease-out animate-in fade-in slide-in-from-bottom-2 fill-mode-both"
            style={{ animationDelay: "200ms", animationDuration: "400ms" }}
          >
            <div className="flex flex-row md:flex-col items-center gap-3 w-full md:w-auto">
              <div className="w-10 h-10 md:w-14 md:h-14 rounded-full bg-ds-blue-50 flex items-center justify-center shrink-0">
                <PenLine className="w-5 h-5 md:w-7 md:h-7 text-ds-blue-600" />
              </div>

              <h3 className="font-semibold font-heading text-navy-900 text-base text-left md:text-center">
                Adaugă manual
              </h3>
            </div>

            <p className="text-xs text-ds-text-500 leading-relaxed text-left md:text-center">
              Completezi tu toate câmpurile. Ideal dacă ai deja datele
              pregătite.
            </p>

            <div className="space-y-1.5 text-left w-full">
              <div className="flex items-center gap-2 text-xs text-ds-text-500">
                <Check className="w-3.5 h-3.5 text-ds-text-400 shrink-0" />
                <span>Control total asupra datelor</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-ds-text-500">
                <Check className="w-3.5 h-3.5 text-ds-text-400 shrink-0" />
                <span>CUI lookup disponibil</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-ds-text-500">
                <Check className="w-3.5 h-3.5 text-ds-text-400 shrink-0" />
                <span>Selectare servicii simplificată</span>
              </div>
            </div>

            <div className="w-full pt-2 mt-auto">
              <Button variant="secondary" className="w-full !text-xs" size="sm">
                Completează manual
                <ArrowRight className="w-4 h-4" />
              </Button>
              <p className="text-xs font-mono text-ds-text-400 italic mt-1">
                timp estimat: &lt;20min
              </p>
            </div>
          </button>

          <button
            onClick={onChooseUpload}
            className="flex flex-col items-start md:items-center text-left md:text-center border border-ds-border-200 rounded-xl p-5 md:p-6 gap-3 bg-white hover:shadow-md hover:scale-[1.02] hover:border-ds-border-300 transition-all duration-300 ease-out animate-in fade-in slide-in-from-bottom-2 fill-mode-both"
            style={{ animationDelay: "300ms", animationDuration: "400ms" }}
          >
            <div className="flex flex-row md:flex-col items-center gap-3 w-full md:w-auto">
              <div className="w-10 h-10 md:w-14 md:h-14 rounded-full bg-ds-green-100 flex items-center justify-center shrink-0">
                <FileUp className="w-5 h-5 md:w-7 md:h-7 text-ds-green-600" />
              </div>

              <h3 className="font-semibold font-heading text-navy-900 text-base text-left md:text-center">
                Încarcă Formular
              </h3>
            </div>

            <p className="text-xs text-ds-text-500 leading-relaxed text-left md:text-center">
              Ai deja un PDF? Extragem automat datele din el.
            </p>

            <div className="space-y-1.5 text-left w-full">
              <div className="flex items-center gap-2 text-xs text-ds-text-500">
                <Check className="w-3.5 h-3.5 text-ds-green-500 shrink-0" />
                <span>Extragere automată din PDF</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-ds-text-500">
                <Check className="w-3.5 h-3.5 text-ds-green-500 shrink-0" />
                <span>Pre-completare câmpuri</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-ds-text-500">
                <Check className="w-3.5 h-3.5 text-ds-green-500 shrink-0" />
                <span>Verificare și ajustare ulterioară</span>
              </div>
            </div>

            <div className="w-full pt-2 mt-auto">
              <Button variant="secondary" className="w-full !text-xs" size="sm">
                Încarcă document
                <Upload className="w-4 h-4" />
              </Button>
              <p className="text-xs font-mono text-ds-text-400 italic mt-1">
                timp estimat: &lt;10min
              </p>
            </div>
          </button>
        </div>

        <p className="text-xs text-ds-text-400 text-center mt-4">
          Poți schimba metoda oricând. Datele se salvează automat.
        </p>
      </DialogContent>
    </Dialog>
  );
}

export default OnboardingModal;
